import Phaser from 'phaser';
import { Card as CardType, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';

// Trump highlight color (muted gold)
const TRUMP_HIGHLIGHT_COLOR = PERSIAN_THEME.colors.gold;
// Selected for discard highlight color (red)
const SELECTED_HIGHLIGHT_COLOR = 0xff4444;

export class Card extends Phaser.GameObjects.Container {
  public readonly cardData: CardType;
  private sprite: Phaser.GameObjects.Image;
  private trumpGlow: Phaser.GameObjects.Graphics | null = null;
  private selectedGlow: Phaser.GameObjects.Graphics | null = null;
  private isSelected: boolean = false;
  private isPlayable: boolean = true;
  private isFaceUp: boolean = true;
  private isTrump: boolean = false;
  private originalY: number;

  constructor(scene: Phaser.Scene, x: number, y: number, cardData: CardType) {
    super(scene, x, y);

    this.cardData = cardData;
    this.originalY = y;

    const textureKey = `card-${cardData.suit}-${cardData.rank}`;
    this.sprite = scene.add.image(0, 0, textureKey);
    // Scale down high-res texture to world coordinates
    this.sprite.setScale(1 / RENDER_SCALE);
    this.add(this.sprite);

    this.setSize(CARD_WIDTH, CARD_HEIGHT);
    this.setInteractive({ useHandCursor: true });

    this.setupInteractions();
    scene.add.existing(this);
  }

  private setupInteractions(): void {
    this.on('pointerover', () => {
      if (this.isPlayable && this.isFaceUp) {
        this.scene.tweens.add({
          targets: this,
          y: this.originalY - 20,
          duration: 100,
          ease: 'Power2',
        });
      }
    });

    this.on('pointerout', () => {
      if (!this.isSelected && this.isPlayable) {
        this.scene.tweens.add({
          targets: this,
          y: this.originalY,
          duration: 100,
          ease: 'Power2',
        });
      }
    });

    this.on('pointerdown', () => {
      if (this.isPlayable && this.isFaceUp) {
        this.emit('cardClicked', this.cardData);
      }
    });
  }

  setPlayable(playable: boolean): void {
    this.isPlayable = playable;
    this.sprite.setAlpha(playable ? 1 : 0.6);
    if (!playable) {
      this.disableInteractive();
    } else {
      this.setInteractive({ useHandCursor: true });
    }
  }

  setSelected(selected: boolean): void {
    if (this.isSelected === selected) return;
    this.isSelected = selected;

    if (selected) {
      this.y = this.originalY - 20;
      // Create red border to indicate selection
      this.selectedGlow = this.scene.add.graphics();
      this.selectedGlow.lineStyle(3, SELECTED_HIGHLIGHT_COLOR, 1);
      this.selectedGlow.strokeRoundedRect(
        -CARD_WIDTH / 2 - 2,
        -CARD_HEIGHT / 2 - 2,
        CARD_WIDTH + 4,
        CARD_HEIGHT + 4,
        CARD_RADIUS
      );
      // Add on top of trump glow but behind sprite
      this.addAt(this.selectedGlow, this.trumpGlow ? 1 : 0);
    } else {
      this.y = this.originalY;
      // Remove selection highlight
      if (this.selectedGlow) {
        this.selectedGlow.destroy();
        this.selectedGlow = null;
      }
    }
  }

  setOriginalY(y: number): void {
    this.originalY = y;
    if (!this.isSelected) {
      this.y = y;
    }
  }

  setTrump(isTrump: boolean): void {
    if (this.isTrump === isTrump) return;
    this.isTrump = isTrump;

    if (isTrump) {
      // Create subtle golden border behind the card
      this.trumpGlow = this.scene.add.graphics();
      this.trumpGlow.lineStyle(2, TRUMP_HIGHLIGHT_COLOR, 0.7);
      this.trumpGlow.strokeRoundedRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
      // Add behind the sprite
      this.addAt(this.trumpGlow, 0);
    } else {
      // Remove glow
      if (this.trumpGlow) {
        this.trumpGlow.destroy();
        this.trumpGlow = null;
      }
    }
  }

  flip(faceUp: boolean): void {
    this.isFaceUp = faceUp;
    const textureKey = faceUp ? `card-${this.cardData.suit}-${this.cardData.rank}` : 'card-back';
    const targetScale = 1 / RENDER_SCALE;

    // Flip animation
    this.scene.tweens.add({
      targets: this.sprite,
      scaleX: 0,
      duration: 100,
      onComplete: () => {
        this.sprite.setTexture(textureKey);
        this.scene.tweens.add({
          targets: this.sprite,
          scaleX: targetScale,
          duration: 100,
        });
      },
    });
  }

  animateTo(targetX: number, targetY: number, duration: number = 300, onComplete?: () => void): void {
    this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration,
      ease: 'Power2',
      onComplete: () => {
        this.originalY = targetY;
        if (onComplete) onComplete();
      },
    });
  }
}
